import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import {
  useGrantOperatorAccess,
  useCancelOperatorInvitation,
  usePersonAccess,
  useResendOperatorInvitation,
  useRevokeOperatorAccess,
} from '../../../platform/api/hooks';
import { str } from '../../../platform/api/utils';
import { usePermissions } from '../../../platform/auth/permissions';
import { Dd, Dt, dlGrid, fmtDate, primaryBtn, secondaryBtn } from './pessoa-detail.support';

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Acesso digital da pessoa. A administração concede o papel operacional fixo:
 * a pessoa recebe o convite por e-mail e opera a rotina da casa, sem gestão.
 */
export function PessoaAccessSection(props: { p: Record<string, unknown> }) {
  const { p } = props;
  const personId = str(p.id);
  const { can } = usePermissions();
  const canManage = can('community.access.manage');
  const { data: accessData } = usePersonAccess(personId, canManage);
  const grant = useGrantOperatorAccess();
  const revoke = useRevokeOperatorAccess();
  const resend = useResendOperatorInvitation();
  const cancelInvitation = useCancelOperatorInvitation();
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const access = accessData;
  const hasIdentity = access?.hasIdentity ?? Boolean(p.userId);
  const isActive = str(p.status) === 'ACTIVE';
  const hasEmail = Boolean(str(p.email));
  const busy =
    grant.isPending || revoke.isPending || resend.isPending || cancelInvitation.isPending;

  const otherRoles = access?.roles ?? [];
  const accessLabel = access?.isAdmin
    ? 'Administração'
    : access?.isOperator
      ? 'Operação'
      : otherRoles.length > 0
        ? `Papel: ${otherRoles.join(', ')}`
        : hasIdentity
          ? 'Sem papel na casa'
          : 'Sem acesso';

  function run(action: () => Promise<unknown>, success: string, failure: string) {
    setFeedback(null);
    action()
      .then(() => {
        setFeedback({ tone: 'ok', text: success });
      })
      .catch((error: unknown) => {
        setFeedback({ tone: 'error', text: errorMessage(error, failure) });
      });
  }

  return (
    <Section title="Acesso digital">
      <dl style={dlGrid}>
        <Dt>Acesso</Dt>
        <Dd>
          <Badge label={accessLabel} variant={otherRoles.length > 0 ? 'success' : 'default'} />
        </Dd>
        <Dt>Login</Dt>
        <Dd>{hasIdentity ? str(p.email, 'Vinculado (sem e-mail cadastrado)') : '—'}</Dd>
        {access?.isOperator && (
          <>
            <Dt>Liberado em</Dt>
            <Dd>{fmtDate(access.grantedAt)}</Dd>
          </>
        )}
      </dl>

      {canManage && access && !access.isAdmin && (
        <div style={{ marginTop: '1rem' }}>
          {!access.isOperator ? (
            <>
              <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 0.75rem' }}>
                O acesso de operação libera calendário, tarefas, estoque, obrigações e comunicados
                da casa. Financeiro, documentos, pessoas, permissões e configurações continuam só
                com a administração. A Zeladoria solicita o convite ao Phoenix A; a entrega de
                e-mail depende da confirmação do próprio serviço.
              </p>
              {!isActive || !hasEmail ? (
                <p style={{ fontSize: '0.8rem', color: 'var(--warning)', margin: 0 }}>
                  {!isActive
                    ? 'Para liberar o acesso, a pessoa precisa estar com status ativo.'
                    : 'Cadastre o e-mail da pessoa para enviar o convite.'}
                </p>
              ) : (
                <button
                  type="button"
                  style={primaryBtn}
                  disabled={busy}
                  onClick={() => {
                    run(
                      () => grant.mutateAsync(personId),
                      'Acesso de operação liberado. O Phoenix A recebeu a solicitação de convite; a entrega do e-mail ainda não foi confirmada.',
                      'Não foi possível liberar o acesso.',
                    );
                  }}
                >
                  {grant.isPending ? 'Liberando…' : 'Liberar acesso de operação'}
                </button>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={secondaryBtn}
                disabled={busy}
                onClick={() => {
                  run(
                    () => resend.mutateAsync(personId),
                    'Solicitação de reenvio registrada no Phoenix A. A entrega do e-mail ainda não foi confirmada.',
                    'Não foi possível reenviar o convite.',
                  );
                }}
              >
                {resend.isPending ? 'Reenviando…' : 'Reenviar convite'}
              </button>
              {confirmRevoke ? (
                <button
                  type="button"
                  style={{ ...secondaryBtn, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                  disabled={busy}
                  onClick={() => {
                    setConfirmRevoke(false);
                    run(
                      () => revoke.mutateAsync(personId),
                      'Acesso de operação retirado.',
                      'Não foi possível retirar o acesso.',
                    );
                  }}
                >
                  Confirmar retirada do acesso
                </button>
              ) : (
                <button
                  type="button"
                  style={secondaryBtn}
                  disabled={busy}
                  onClick={() => {
                    setConfirmRevoke(true);
                  }}
                >
                  Retirar acesso
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {canManage && access?.invitationStatus && (
        <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.75rem' }}>
          Convite no Phoenix A:{' '}
          {access.invitationStatus === 'pending'
            ? 'pendente'
            : access.invitationStatus === 'activated'
              ? 'ativado'
              : access.invitationStatus === 'expired'
                ? 'expirado'
                : access.invitationStatus === 'cancelled'
                  ? 'cancelado'
                  : 'não confirmado'}
        </p>
      )}

      {canManage &&
        !access?.isOperator &&
        hasIdentity &&
        (access?.invitationStatus === 'pending' || access?.invitationStatus === 'unknown') && (
          <button
            type="button"
            style={secondaryBtn}
            disabled={busy}
            onClick={() => {
              run(
                () => cancelInvitation.mutateAsync(personId),
                'Estado do convite reconciliado com o Phoenix A.',
                'Não foi possível reconciliar o convite com o Phoenix A.',
              );
            }}
          >
            {cancelInvitation.isPending ? 'Verificando convite…' : 'Reconciliar convite pendente'}
          </button>
        )}

      {feedback && (
        <p
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          style={{
            fontSize: '0.8rem',
            marginTop: '0.75rem',
            color: feedback.tone === 'error' ? 'var(--danger)' : 'var(--success)',
          }}
        >
          {feedback.text}
        </p>
      )}
    </Section>
  );
}
